// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { ApiParameter, del, doesNotHaveExpand, get, patch, post } from "@insite/client-framework/Services/ApiService";
import {
    AddAllWishListLinesApiParameter,
    GetWishListApiParameter,
    GetWishListLinesApiParameter,
    UpdateWishListApiParameter,
} from "@insite/client-framework/Services/WishListService";
import { WishListLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";

export interface AddWishListApiParameter extends ApiParameter {
    name?: string;
    description?: string;
    groups?: any;
}

export interface RequestQuoteFromWishListApiParameter extends ApiParameter {
    wishlistId: string;
    groupsProductsInfo?: SafeDictionary<any>;
}

export interface PdfSettingsApiParameter extends ApiParameter {
    id: string;
    withCoverPage?: boolean;
    withTableOfContent?: boolean;
    withAboutUs?: boolean;
    withSpecSheet?: boolean;
    productsInlineFormat?: boolean;
    customerEmailAddress?: string;
    preparedFor?: string;
    consultant?: string;
    projectName?: string;
    phone?: string;
    pdfType: "wishlist" | "quote";
    sendPdfByEmail: boolean;
    productsImages?: { productErpNumber: string; id: string }[];
    ThirdPartyQuoteClientName?: string;
    ThirdPartyQuoteClientStreetAddress1?: string;
    ThirdPartyQuoteClientStreetAddress2?: string;
    ThirdPartyQuoteClientCity?: string;
    ThirdPartyQuoteClientProvince?: string;
    ThirdPartyQuoteClientPostalCode?: string;
    ThirdPartyQuoteWebsite?: string;
    ThirdPartyQuoteClientEmail?: string;
    ThirdPartyQuoteClientPhoneNumber?: string;
    ThirdPartyQuoteFreightCharges?: number;
    ThirdPartyQuoteLabourCharges?: number;
    ThirdPartyQuoteTaxDisplayOption?: "Hst" | "Gst" | "NoTax";
    ThirdPartyQuotePriceDisplayOption?: "ShowProductPrices" | "HideProductPrices";
    ThirdPartyQuoteExpirationDate?: string | null;
    includePrice?: boolean;
    generalNotes?: string;
}

const requestQuoteFromWishListUrl = "api/custom/wishlist/RequestQuote";
const requestCustomQuoteFromWishListUrl = "/api/custom/wishlist/ConvertToCustomQuote";
const wishListUrl = "/api/v1/wishlists";
const pdfGenerationUrl = "/api/custom/PdfGeneration";
const customWishListUrl = "/api/custom/wishlist/";

export function addWishList(parameter: AddWishListApiParameter) {
    const groups = {
        groups: parameter.groups.join(),
    };
    return post<WishListModel>(wishListUrl, {
        name: parameter.name,
        description: parameter.description,
        properties: groups,
    } as unknown as WishListModel);
}

export async function updateWishList(parameter: UpdateWishListApiParameter) {
    const wishListId = parameter.wishList?.id || parameter.wishListId;
    const wishListToUpdate = parameter.wishList || {
        name: parameter.name,
        description: parameter.description,
    };
    const wishList = await patch<WishListModel>(`${wishListUrl}/${wishListId}`, wishListToUpdate);
    cleanWishList(wishList);
    return wishList;
}

export async function requestQuoteForWishList(parameter: RequestQuoteFromWishListApiParameter) {
    const quote = await patch<any>(`${requestQuoteFromWishListUrl}`, parameter);
    return quote;
}

export async function requestCustomQuoteForWishList(parameter: RequestQuoteFromWishListApiParameter) {
    const quote = await post<any>(`${requestCustomQuoteFromWishListUrl}`, parameter);
    return quote;
}

export async function setPdfSettings(parameter: PdfSettingsApiParameter) {
    try {
        const result = await post<PdfSettingsApiParameter, { pdfFileUrl?: string }>(`${pdfGenerationUrl}/GeneratePdf`, parameter);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            error,
        };
    }
}

export function addAllWishListLines(
    parameter: AddAllWishListLinesApiParameter & { refreshWishListHeaderGroups?: boolean },
) {
    const wishListLinesUri = parameter.wishList.wishListLinesUri.substr(
        parameter.wishList.wishListLinesUri.indexOf("/api/v1"),
    );

    let params = {
        changedListLineQuantities: parameter.changedListLineQuantities,
        wishListLines: [],
        pagination: null,
    } as any;

    if (parameter.refreshWishListHeaderGroups) {
        params = {
            ...params,
            properties: {
                refreshWishListHeaderGroups: true,
            },
        };
    }

    return post(`${wishListLinesUri}/batch/${parameter.copyFromWishListId}`, params);
}

function cleanWishList(wishList: WishListModel, parameter?: { expand?: string[]; exclude?: string[] }) {
    if (doesNotHaveExpand(parameter, "schedule")) {
        wishList.schedule = null;
    }
    if (doesNotHaveExpand(parameter, "sharedUsers")) {
        wishList.sharedUsers = null;
    }
    if (parameter?.exclude && parameter?.exclude.indexOf("listLines") > -1) {
        wishList.wishListLineCollection = null;
        wishList.wishListLinesCount = -1;
    }
}

export async function setDefaultPdfSettingsValues(parameter: PdfSettingsApiParameter) {
    try {
        const result = await post<PdfSettingsApiParameter>(`${pdfGenerationUrl}/SetDefaultValues`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error,
        };
    }
}

export interface WishListGroupingItem {
    groupLineId: string;
    groupName: string;
    originalGroupName?: string;
    notes?: string;
    qtyOrdered: number;
    sortOrder: number;
}

export interface WishListCustomLineModel {
    wishListLineId: string;
    groupLineId: string;
    sortOrder?: number;
    qty?: number;
    notes?: string;
}

export interface MoveCopyOrDeleteItem extends ApiParameter {
    wishListId: string;
    newGroupName: string;
    linesModel: WishListCustomLineModel[];
}

export interface UpdateLineInfo extends ApiParameter {
    wishListId: string;
    updateSortOrder?: boolean;
    updateQty?: boolean;
    updateNote?: boolean;
    linesModel: WishListCustomLineModel[];
}

export async function updateLineInfo(paramter: UpdateLineInfo) {
    try {
        await patch<any>(`${customWishListUrl}/updateLineItem`, paramter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: error.errorMessage,
        };
    }
}

export async function moveLines(parameter: MoveCopyOrDeleteItem) {
    try {
        await patch<any>(`${customWishListUrl}/move`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: error.errorMessage,
        };
    }
}

export async function copyLines(parameter: MoveCopyOrDeleteItem) {
    try {
        await patch<any>(`${customWishListUrl}/copy`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: error.errorMessage,
        };
    }
}

// Add timestamp to remove caching
export function getWishListLines(parameter: GetWishListLinesApiParameter) {
    const timestamp = new Date().getTime();
    const { wishListId, ...newParameter } = { ...parameter, timestamp };
    return get<WishListLineCollectionModel>(`${wishListUrl}/${wishListId}/wishlistlines`, newParameter);
}

// Add timestamp to remove caching
export async function getWishList(parameter: GetWishListApiParameter) {
    const timestamp = new Date().getTime();
    const { wishListId, ...newParameter } = { ...parameter, timestamp };
    const wishList = await get<WishListModel>(`${wishListUrl}/${wishListId}`, newParameter);
    cleanWishList(wishList, parameter);
    return wishList;
}

export interface RenameGroup extends ApiParameter {
    wishListId: string;
    originalGroupName: string;
    newGroupName: string;
}

export async function renameGroup(parameter: RenameGroup) {
    try {
        await patch<any>(`${customWishListUrl}/RenameGroup`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error,
        };
    }
}

export interface DeleteWishlistLine extends ApiParameter {
    wishListId: string;
    groupLineId: string;
}

export async function deleteWishlistLine(params: DeleteWishlistLine) {
    try {
        await del(`${customWishListUrl}/${params.wishListId}/DeleteItem/${params.groupLineId}`);
        return {
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                error: (error as any).errorMessage,
            };
        }
        return {
            successful: true,
        };
    }
}

export interface DeletWishlistGroup extends ApiParameter {
    wishListId: string;
    groupName: string;
}

export async function deleteWishlistGroup(params: DeletWishlistGroup) {
    try {
        await del(`${customWishListUrl}/${params.wishListId}/DeleteGroup/${params.groupName}`);
        return {
            successful: true,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                error: (error as any).errorMessage,
            };
        }
        return {
            successful: true,
        };
    }
}
